import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CalendarClock, Inbox } from "lucide-react"
import { useDeferredValue, useEffect, useMemo } from "react"

import { Button } from "@/components/button"
import { EmptyState } from "@/components/shared/empty-state"
import { FadeIn } from "@/components/shared/fade-in"
import {
  type ColumnDef,
  ResponsiveTable,
} from "@/components/shared/responsive-table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  absenceTrackingApi,
  type AbsenceRequest,
} from "@/lib/absence-tracking-api"
import {
  formatGuildDateRange,
  formatGuildDateTime,
} from "@/lib/guild-date-format"
import { memberDisplayLabel } from "@/lib/member-display-label"
import {
  type AbsenceReviewUiStore,
  type PendingSortOption,
  useReviewFocusedPendingIndex,
  useReviewPendingSort,
  useReviewQueueFilter,
  useReviewSearchQuery,
  useReviewSelectedPendingIds,
} from "@/lib/stores/absence-review-ui-store"

const adminAbsenceOverviewQueryKey = ["absence-tracking", "admin"] as const
const ABSENCE_REVIEW_PREFERENCES_KEY = "beagle:absence-review:preferences"

export function PendingRequestsCard({
  store,
  pendingRequests,
  reviewRequired,
}: {
  store: AbsenceReviewUiStore
  pendingRequests: AbsenceRequest[]
  reviewRequired: boolean
}) {
  const queryClient = useQueryClient()
  const searchQuery = useReviewSearchQuery(store)
  const queueFilter = useReviewQueueFilter(store)
  const pendingSort = useReviewPendingSort(store)
  const selectedPendingIds = useReviewSelectedPendingIds(store)
  const focusedPendingIndex = useReviewFocusedPendingIndex(store)

  const deferredSearchQuery = useDeferredValue(searchQuery)
  const normalizedSearch = deferredSearchQuery.trim().toLowerCase()

  // Load preferences from localStorage on mount
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const raw = localStorage.getItem(ABSENCE_REVIEW_PREFERENCES_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as {
        queueFilter?: "all" | "pending" | "approved"
        pendingSort?: PendingSortOption
      }
      if (parsed.queueFilter)
        store.getState().setQueueFilter(parsed.queueFilter)
      if (parsed.pendingSort)
        store.getState().setPendingSort(parsed.pendingSort)
    } catch {
      // ignore invalid localStorage payload
    }
  }, [store])

  // Persist preferences to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      localStorage.setItem(
        ABSENCE_REVIEW_PREFERENCES_KEY,
        JSON.stringify({ queueFilter, pendingSort }),
      )
    } catch {
      // ignore localStorage write failures
    }
  }, [queueFilter, pendingSort, store])

  // Filter + sort
  const filteredPendingRequests = useMemo(() => {
    if (!normalizedSearch) return pendingRequests
    return pendingRequests.filter((item) =>
      [item.member?.displayName, item.memberId, item.reason]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(normalizedSearch)),
    )
  }, [normalizedSearch, pendingRequests])

  const sortedPendingRequests = useMemo(() => {
    const items = [...filteredPendingRequests]
    if (pendingSort === "member") {
      items.sort((a, b) =>
        memberDisplayLabel({
          displayName: a.member?.displayName,
          memberId: a.memberId,
        }).localeCompare(
          memberDisplayLabel({
            displayName: b.member?.displayName,
            memberId: b.memberId,
          }),
        ),
      )
      return items
    }
    const direction = pendingSort === "submitted_desc" ? -1 : 1
    items.sort(
      (a, b) =>
        (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) *
        direction,
    )
    return items
  }, [filteredPendingRequests, pendingSort])

  const pendingIds = useMemo(
    () => new Set(sortedPendingRequests.map((item) => item.id)),
    [sortedPendingRequests],
  )

  // Prune stale selection when data changes
  useEffect(() => {
    store.getState().pruneStaleIds(pendingIds)
  }, [pendingIds, store])

  // Clamp focused index
  useEffect(() => {
    if (sortedPendingRequests.length === 0) {
      store.getState().setFocusedPendingIndex(0)
      return
    }
    store
      .getState()
      .setFocusedPendingIndex((current) =>
        Math.min(current, sortedPendingRequests.length - 1),
      )
  }, [sortedPendingRequests.length, store])

  // Mutations
  const reviewMutation = useMutation({
    mutationFn: ({
      requestId,
      decision,
    }: {
      requestId: string
      decision: "approved" | "rejected"
    }) => absenceTrackingApi.review(requestId, decision),
    onSuccess: async (_item, variables) => {
      store.getState().setMessage({
        tone: "success",
        text:
          variables.decision === "approved"
            ? "Absence activated"
            : "Absence noted",
      })
      await queryClient.invalidateQueries({
        queryKey: adminAbsenceOverviewQueryKey,
      })
    },
    onError: (error) => {
      store.getState().setMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to review absence submission",
      })
    },
  })

  const bulkReviewMutation = useMutation({
    mutationFn: async ({
      requestIds,
      decision,
    }: {
      requestIds: string[]
      decision: "approved" | "rejected"
    }) => {
      for (const requestId of requestIds) {
        await absenceTrackingApi.review(requestId, decision)
      }
      return { requestIds, decision }
    },
    onSuccess: async (result) => {
      store.getState().setMessage({
        tone: "success",
        text: `${result.decision === "approved" ? "Activated" : "Noted"} ${result.requestIds.length} absence${result.requestIds.length === 1 ? "" : "s"}`,
      })
      store.getState().setVisibleSelected([], false)
      await queryClient.invalidateQueries({
        queryKey: adminAbsenceOverviewQueryKey,
      })
    },
    onError: (error) => {
      store.getState().setMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to apply bulk review action",
      })
    },
  })

  const isReviewPending =
    reviewMutation.isPending || bulkReviewMutation.isPending

  const confirmSingleDecision = (decision: "approved" | "rejected") => {
    const label = decision === "approved" ? "activate" : "note"
    return window.confirm(`Are you sure you want to ${label} this absence?`)
  }

  // Keyboard navigation
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tagName = target?.tagName?.toLowerCase()
      const isTypingTarget =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(target?.isContentEditable)

      if (isTypingTarget) return

      const key = event.key.toLowerCase()
      if (key === "j") {
        event.preventDefault()
        store
          .getState()
          .setFocusedPendingIndex((current) =>
            Math.min(
              current + 1,
              Math.max(0, sortedPendingRequests.length - 1),
            ),
          )
        return
      }

      if (key === "k") {
        event.preventDefault()
        store
          .getState()
          .setFocusedPendingIndex((current) => Math.max(0, current - 1))
        return
      }

      const targetItem = sortedPendingRequests[focusedPendingIndex]
      if (!targetItem || isReviewPending) return

      if (key === "a") {
        event.preventDefault()
        if (!confirmSingleDecision("approved")) return
        void reviewMutation.mutateAsync({
          requestId: targetItem.id,
          decision: "approved",
        })
        return
      }

      if (key === "r") {
        event.preventDefault()
        if (!confirmSingleDecision("rejected")) return
        void reviewMutation.mutateAsync({
          requestId: targetItem.id,
          decision: "rejected",
        })
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [
    focusedPendingIndex,
    isReviewPending,
    reviewMutation,
    sortedPendingRequests,
    store,
  ])

  const applyBulkDecision = (decision: "approved" | "rejected") => {
    const requestIds = sortedPendingRequests
      .filter((item) => selectedPendingIds.has(item.id))
      .map((item) => item.id)
    if (requestIds.length === 0) return

    const label = decision === "approved" ? "activate" : "note"
    const confirmed = window.confirm(
      `Are you sure you want to ${label} ${requestIds.length} absence${requestIds.length === 1 ? "" : "s"}?`,
    )
    if (!confirmed) return

    void bulkReviewMutation.mutateAsync({ requestIds, decision })
  }

  const selectedPendingCount = selectedPendingIds.size
  const allPendingVisibleSelected =
    sortedPendingRequests.length > 0 &&
    sortedPendingRequests.every((item) => selectedPendingIds.has(item.id))

  if (!reviewRequired || (queueFilter !== "all" && queueFilter !== "pending")) {
    return null
  }

  return (
    <FadeIn delay={0.05}>
      <Card>
        <CardHeader>
          <CardTitle>Pending submissions</CardTitle>
          <CardDescription>
            Review new absence submissions before they become active. Keyboard
            shortcuts: J/K (focus next/previous), A (activate), R (note).
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border/60 bg-card/35 px-3 py-2">
            <div className="text-sm text-muted-foreground">
              {selectedPendingCount > 0
                ? `${selectedPendingCount} selected`
                : "Select rows to run bulk actions"}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={sortedPendingRequests.length === 0}
                onClick={() => {
                  store.getState().setVisibleSelected(
                    sortedPendingRequests.map((item) => item.id),
                    !allPendingVisibleSelected,
                  )
                }}
              >
                {allPendingVisibleSelected ? "Clear visible" : "Select visible"}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={selectedPendingCount === 0 || isReviewPending}
                onClick={() => applyBulkDecision("approved")}
              >
                Activate selected
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={selectedPendingCount === 0 || isReviewPending}
                onClick={() => applyBulkDecision("rejected")}
              >
                Note selected
              </Button>
            </div>
          </div>
          <PendingRequestsTable
            items={sortedPendingRequests}
            pendingAction={reviewMutation.variables}
            disabled={isReviewPending}
            focusedRequestId={
              sortedPendingRequests[focusedPendingIndex]?.id ?? null
            }
            selectedRequestIds={selectedPendingIds}
            onToggleRequest={(requestId, checked) => {
              store.getState().toggleRequestId(requestId, checked)
            }}
            onToggleAll={(checked) => {
              store.getState().setVisibleSelected(
                sortedPendingRequests.map((item) => item.id),
                checked,
              )
            }}
            onApprove={(requestId) =>
              confirmSingleDecision("approved")
                ? void reviewMutation.mutateAsync({
                    requestId,
                    decision: "approved",
                  })
                : undefined
            }
            onReject={(requestId) =>
              confirmSingleDecision("rejected")
                ? void reviewMutation.mutateAsync({
                    requestId,
                    decision: "rejected",
                  })
                : undefined
            }
          />
        </CardContent>
      </Card>
    </FadeIn>
  )
}

function PendingRequestsTable({
  items,
  pendingAction,
  disabled,
  focusedRequestId,
  selectedRequestIds,
  onToggleRequest,
  onToggleAll,
  onApprove,
  onReject,
}: {
  items: AbsenceRequest[]
  pendingAction:
    | { requestId: string; decision: "approved" | "rejected" }
    | undefined
  disabled: boolean
  focusedRequestId: string | null
  selectedRequestIds: ReadonlySet<string>
  onToggleRequest: (requestId: string, checked: boolean) => void
  onToggleAll: (checked: boolean) => void
  onApprove: (requestId: string) => void
  onReject: (requestId: string) => void
}) {
  const allSelected =
    items.length > 0 && items.every((item) => selectedRequestIds.has(item.id))

  const columns = useMemo<Array<ColumnDef<(typeof items)[number]>>>(
    () => [
      {
        header: (
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => onToggleAll(Boolean(checked))}
            aria-label="Select all pending submissions"
          />
        ),
        headerClassName: "w-10",
        cell: (item) => (
          <Checkbox
            checked={selectedRequestIds.has(item.id)}
            onCheckedChange={(checked) =>
              onToggleRequest(item.id, Boolean(checked))
            }
            aria-label={`Select submission ${item.id}`}
          />
        ),
        mobileCard: (item) => (
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-foreground">
                {memberDisplayLabel({
                  displayName: item.member?.displayName,
                  memberId: item.memberId,
                })}
              </div>
              <div className="text-xs text-muted-foreground">
                {item.memberId}
              </div>
            </div>
            <Checkbox
              checked={selectedRequestIds.has(item.id)}
              onCheckedChange={(checked) =>
                onToggleRequest(item.id, Boolean(checked))
              }
              aria-label={`Select submission ${item.id}`}
            />
          </div>
        ),
      },
      {
        header: "Member",
        isRowHeader: true,
        cell: (item) => (
          <div>
            {memberDisplayLabel({
              displayName: item.member?.displayName,
              memberId: item.memberId,
            })}
            <div className="text-xs font-normal text-muted-foreground">
              {item.memberId}
            </div>
          </div>
        ),
      },
      {
        header: "Range",
        cell: (item) => (
          <div className="flex items-center gap-2">
            <CalendarClock className="size-4 text-muted-foreground" />
            {formatGuildDateRange(item.startAt, item.endAt)}
          </div>
        ),
        mobileCard: (item) => (
          <div className="grid gap-1.5 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarClock className="size-4" />
              <span>{formatGuildDateRange(item.startAt, item.endAt)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Reason: </span>
              <span>{item.reason ?? "-"}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Submitted {formatGuildDateTime(item.createdAt)}
            </div>
          </div>
        ),
      },
      {
        header: "Reason",
        cell: (item) => (
          <span
            className="block max-w-48 truncate"
            title={item.reason ?? undefined}
          >
            {item.reason ?? "-"}
          </span>
        ),
        mobileCard: (item) => (
          <div>
            <span className="text-muted-foreground">Reason: </span>
            <span className="line-clamp-2">{item.reason ?? "-"}</span>
          </div>
        ),
      },
      {
        header: "Submitted",
        cellClassName: "text-muted-foreground",
        cell: (item) => formatGuildDateTime(item.createdAt),
      },
      {
        header: "Actions",
        cell: (item) => (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={disabled}
              onClick={() => onApprove(item.id)}
            >
              {pendingAction?.requestId === item.id &&
              pendingAction.decision === "approved"
                ? "Activating..."
                : "Activate"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={disabled}
              onClick={() => onReject(item.id)}
            >
              {pendingAction?.requestId === item.id &&
              pendingAction.decision === "rejected"
                ? "Noting..."
                : "Note"}
            </Button>
          </div>
        ),
        mobileCard: (item) => (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              size="sm"
              disabled={disabled}
              onClick={() => onApprove(item.id)}
            >
              {pendingAction?.requestId === item.id &&
              pendingAction.decision === "approved"
                ? "Activating..."
                : "Activate"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={disabled}
              onClick={() => onReject(item.id)}
            >
              {pendingAction?.requestId === item.id &&
              pendingAction.decision === "rejected"
                ? "Noting..."
                : "Note"}
            </Button>
          </div>
        ),
      },
    ],
    [
      allSelected,
      disabled,
      onApprove,
      onReject,
      onToggleAll,
      onToggleRequest,
      pendingAction,
      selectedRequestIds,
    ],
  )

  return (
    <ResponsiveTable
      columns={columns}
      data={items}
      keyFn={(item) => item.id}
      caption="Pending absence submissions"
      emptyState={
        <EmptyState
          icon={<Inbox className="size-4" />}
          title="No pending submissions"
          description="New submissions will appear here for officer review."
        />
      }
      rowClassName={(item) =>
        focusedRequestId === item.id
          ? "bg-primary/8 outline outline-primary/35"
          : undefined
      }
      mobileHeader={
        <div className="flex items-center justify-between rounded-xl border border-border/60 bg-card/40 px-3 py-2">
          <div className="text-sm text-muted-foreground">
            Select all visible
          </div>
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => onToggleAll(Boolean(checked))}
            aria-label="Select all pending submissions"
          />
        </div>
      }
    />
  )
}
